import { createContext, useContext } from "react";
import { featureOn, hasPermission, roleCan, type Action, type Role } from "@szerviz/core";
import type { DataStore, MfaRequirement, MfaStatus, MySubscription, Workshop } from "./data";

export interface AppContextValue {
  store: DataStore;
  workshop: Workshop;
  /** Re-reads the workshop (after its settings changed). */
  reloadWorkshop: () => Promise<void>;
  /** Whether the question mark help is switched on for this user. */
  helpEnabled: boolean;
  setHelpEnabled: (on: boolean) => Promise<void>;
  /** The signed in user's role in this workshop (null while loading). */
  role: Role | null;
  /** The signed in user's id (own work orders, "you" in the lists). */
  userId?: string;
  /** Moves to a page (and optionally one record on it) through the address hash. */
  go: (page: string, id?: string) => void;
  /** One step back to the previous screen of the program; to `fallback` when there is none. */
  back: (fallback: string) => void;
  /** Two-step sign in of the signed in user (Settings shows and changes it). */
  mfa?: { status: MfaStatus; requirement: MfaRequirement };
  reloadMfa?: () => Promise<void>;
  /** The workshop's subscription (trial, Profi week, plan; null while loading or when it cannot be read). */
  subscription?: MySubscription | null;
  reloadSubscription?: () => Promise<void>;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const v = useContext(AppContext);
  if (!v) throw new Error("AppContext missing");
  return v;
}

/** Help is optional: components outside the shell (login) render without it. */
export function useHelpEnabled(): boolean {
  return useContext(AppContext)?.helpEnabled ?? false;
}

/** A permission list (reopen, delete, the two mechanic switches), as public.has_permission() decides it. */
export function can(role: Role | null, workshop: Workshop, permission: string): boolean {
  return hasPermission(role, workshop.permissions, permission);
}

/** What the signed in user may do here, as public.can_do() decides it (the database enforces it). */
export function useCan(action: Action): boolean {
  const { role, workshop } = useApp();
  return roleCan(role, workshop.permissions, action);
}

/** A switchable feature works in this workshop: in the package and switched on. */
export function useFeature(key: string): boolean {
  const { workshop } = useApp();
  return featureOn(workshop, key);
}
